import { UseFormReturn } from 'react-hook-form'
import { useEffect, useRef } from 'react'
import useJobFilters from '@hooks/useJobFilters'
import { FilterTypes } from '@templates/ListPageLayout/Filters/Filter'
import { FiltersProps } from '@templates/ListPageLayout/Filters'
import RISK_MAP_STATUSES from '@const/riskMapStatuses'
import useDangerWorkPlaces from '@hooks/useDangerWorkPlaces'
import useDangerKinds from '@hooks/useDangerKinds'
import useDangerHazards from '@hooks/useDangerHazards'

enum ExpireValues {
  EXPIRED = 'true',
  NOT_EXPIRED = 'false',
}

enum ReviseValues {
  NOT_REVISED = 'true',
  ACTIVE = 'false',
}

const useRiskMapsFilters = (methods: UseFormReturn): FiltersProps => {
  const { company, filial, division, position } = useJobFilters(methods)

  const {
    data: dangerWorkPlacesData,
    isLoading: dangerWorkPlacesIsLoading,
    setFilters: dangerWorkPlacesSetFilters,
    setPage: dangerWorkPlacesSetPage,
  } = useDangerWorkPlaces()
  const {
    items: dangerWorkPlacesDataItems = [],
    length: dangerWorkPlacesDataLength = 0,
  } = dangerWorkPlacesData || {}

  const {
    data: dangerKindsData,
    isLoading: dangerKindsIsLoading,
    setFilters: dangerKindsSetFilters,
    setPage: dangerKindsSetPage,
  } = useDangerKinds()
  const {
    items: dangerKindsDataItems = [],
    length: dangerKindsDataLength = 0,
  } = dangerKindsData || {}

  const {
    data: dangerHazardsData,
    isLoading: dangerHazardsIsLoading,
    setFilters: dangerHazardsSetFilters,
    setPage: dangerHazardsSetPage,
  } = useDangerHazards()
  const {
    items: dangerHazardsDataItems = [],
    length: dangerHazardsDataLength = 0,
  } = dangerHazardsData || {}

  const prevRelationsRef = useRef<any>(null)

  const kind: string | undefined = methods.watch('kind')?.value
  const workPlace: string | undefined = methods.watch('workPlace')?.value
  const hazard: string | undefined = methods.watch('hazard')?.value

  const relations = {
    kind,
    workPlace,
    hazard,
  }
  const stringRelations = JSON.stringify(relations)

  useEffect(() => {
    if (prevRelationsRef.current === stringRelations) return
    dangerKindsSetFilters({ relations })
    dangerHazardsSetFilters({ relations })
    dangerWorkPlacesSetFilters({ relations })

    prevRelationsRef.current = stringRelations
  }, [stringRelations])

  const filters: FiltersProps = [
    {
      filterType: FilterTypes.HIDDEN,
      fieldName: 'id',
    },
  ]

  if (company) {
    filters.push({
      ...company,
      fieldNameForSubmit: 'jobPosition.subdivision.companyBranch.company',
    })
  }

  if (filial) {
    filters.push({
      ...filial,
      fieldNameForSubmit: 'jobPosition.subdivision.companyBranch',
    })
  }

  if (division) {
    filters.push({ ...division, fieldNameForSubmit: 'jobPosition.subdivision' })
  }

  if (position) {
    filters.push({ ...position, fieldNameForSubmit: 'jobPosition' })
  }

  filters.push(
    {
      filterType: FilterTypes.SELECT,
      fieldName: 'status',
      placeholder: 'Статус',
      options: RISK_MAP_STATUSES,
      withoutFetchOptions: true,
    },
    {
      options: dangerWorkPlacesDataItems,
      length: dangerWorkPlacesDataLength,
      isLoading: dangerWorkPlacesIsLoading,
      placeholder: 'Работы / Место / Авария',
      fieldName: 'workPlace',
      fieldNameForSubmit: 'dangers.workPlace',
      filterType: FilterTypes.SELECT_SEARCH,
      setFilters: dangerWorkPlacesSetFilters,
      setPage: dangerWorkPlacesSetPage,
      dropdownWidth: 500,
    },
    {
      options: dangerKindsDataItems,
      length: dangerKindsDataLength,
      isLoading: dangerKindsIsLoading,
      placeholder: 'Вид опасности',
      fieldName: 'kind',
      fieldNameForSubmit: 'dangers.kind',
      filterType: FilterTypes.SELECT_SEARCH,
      setFilters: dangerKindsSetFilters,
      setPage: dangerKindsSetPage,
      dropdownWidth: 500,
    },
    {
      options: dangerHazardsDataItems,
      length: dangerHazardsDataLength,
      isLoading: dangerHazardsIsLoading,
      placeholder: 'Опасность',
      fieldName: 'hazard',
      fieldNameForSubmit: 'dangers.hazard',
      filterType: FilterTypes.SELECT_SEARCH,
      setFilters: dangerHazardsSetFilters,
      setPage: dangerHazardsSetPage,
      dropdownWidth: 500,
    },
    {
      filterType: FilterTypes.SELECT_SEARCH,
      fieldName: 'significance',
      placeholder: 'Значимость риска',
      options: [
        {
          label: 'Низкая',
          value: 1,
        },
        {
          label: 'Средняя',
          value: 2,
        },
        {
          label: 'Высокая',
          value: 3,
        },
      ],
      withoutFetchOptions: true,
    },
    {
      filterType: FilterTypes.DATE,
      fieldName: 'approvedAt[after]',
      placeholder: 'Дата утверждения от',
    },
    {
      filterType: FilterTypes.DATE,
      fieldName: 'approvedAt[before]',
      placeholder: 'Дата утверждения до',
    },
    {
      filterType: FilterTypes.SELECT,
      fieldName: 'overdue',
      placeholder: 'Пересмотр',
      options: [
        {
          label: 'Просрочено',
          value: ReviseValues.NOT_REVISED,
        },
        {
          label: 'Активно',
          value: ReviseValues.ACTIVE,
        },
      ],
      withoutFetchOptions: true,
    },
    {
      filterType: FilterTypes.SELECT,
      fieldName: 'expire',
      placeholder: 'Обновление СИЗ',
      options: [
        {
          value: ExpireValues.EXPIRED,
          label: 'Срок СИЗ истёк',
        },
        {
          value: ExpireValues.NOT_EXPIRED,
          label: 'Срок СИЗ истекает',
        },
      ],
      withoutFetchOptions: true,
      transformValue: (filter) => {
        if (filter.expire[0] === ExpireValues.EXPIRED) {
          return {
            individualProtectionMeanExpires: null,
            individualProtectionMeanExpired: 'true',
          }
        } else if (filter.expire[0] === ExpireValues.NOT_EXPIRED) {
          return {
            individualProtectionMeanExpired: null,
            individualProtectionMeanExpires: 'true',
          }
        }

        return {
          individualProtectionMeanExpires: null,
          individualProtectionMeanExpired: null,
        }
      },
    }
  )

  return filters
}

export default useRiskMapsFilters
